import { useCallback, useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  getSubscriptionRelinkPreview,
  linkSubscription,
  type MemberMembership,
  type SubscriptionRelinkBlocker,
  type SubscriptionRelinkPreview,
} from '@/api/members';
import { serverMessage } from '@/api/serverMessage';
import { Icon } from '@/theme';

interface LinkSubscriptionDialogProps {
  contactId: string;
  subscriptionId: string;
  open: boolean;
  onClose: () => void;
  onLinked: (membership: MemberMembership) => void;
}

type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; preview: SubscriptionRelinkPreview }
  | { status: 'error'; message: string };

export function LinkSubscriptionDialog({
  contactId,
  subscriptionId,
  open,
  onClose,
  onLinked,
}: LinkSubscriptionDialogProps) {
  if (!open) return null;
  return (
    <LinkSubscriptionDialogBody
      key={`${contactId}:${subscriptionId}`}
      contactId={contactId}
      subscriptionId={subscriptionId}
      onClose={onClose}
      onLinked={onLinked}
    />
  );
}

function LinkSubscriptionDialogBody({
  contactId,
  subscriptionId,
  onClose,
  onLinked,
}: Omit<LinkSubscriptionDialogProps, 'open'>) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadPreview = useCallback(() => {
    setState({ status: 'loading' });
    setSubmitError(null);
    void getSubscriptionRelinkPreview(contactId, subscriptionId)
      .then((preview) => setState({ status: 'ready', preview }))
      .catch((error: unknown) =>
        setState({
          status: 'error',
          message: serverMessage(error) ?? 'Subscription preview could not be loaded.',
        }),
      );
  }, [contactId, subscriptionId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const preview = state.status === 'ready' ? state.preview : null;
  const blocker = preview?.blocker ?? null;
  const blockerMessage = blocker !== null ? blockerCopy(blocker) : null;
  const confirmDisabled = state.status !== 'ready' || blocker !== null || submitting;

  async function confirm() {
    if (confirmDisabled) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const membership = await linkSubscription(contactId, subscriptionId);
      onLinked(membership);
    } catch (error) {
      setSubmitError(serverMessage(error) ?? 'Subscription could not be linked.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId}>Link subscription</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Review the existing subscription before rebuilding the member&apos;s membership.
          </Typography>

          {state.status === 'loading' && (
            <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading subscription preview...</Typography>
            </Stack>
          )}

          {state.status === 'error' && (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button color="inherit" size="small" onClick={loadPreview}>
                  Retry
                </Button>
              }
            >
              {state.message}
            </Alert>
          )}

          {preview !== null && (
            <>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>What was found</Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Fact label="Subscription" value={preview.subscriptionId ?? subscriptionId} />
                  <Fact label="Status" value={preview.status ?? '-'} />
                  <Fact label="Plan" value={preview.tier ?? '-'} />
                  <Fact label="Payment subscription" value={preview.pspSubscriptionId ?? '-'} />
                  <Fact label="Created" value={preview.createdAt ?? '-'} />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>What linking will do</Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  Rebuild the membership on this subscription, set the plan to {preview.tier ?? 'the resolved plan'},
                  and mark the membership Active.
                </Typography>
              </Box>

              <Alert severity="warning" icon={<Icon name="checklist" />}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Operator checklist</Typography>
                <Box component="ul" sx={{ pl: 2.5, my: 0.75 }}>
                  <li>Confirm the plan/tier is correct.</li>
                  <li>Expiration is approximate until the next renewal.</li>
                  <li>Credits are NOT restored. Check the credit balance and grant manually if needed.</li>
                </Box>
              </Alert>

              {blockerMessage !== null && (
                <Alert severity="error" role="alert">
                  {blockerMessage}
                </Alert>
              )}
            </>
          )}

          {submitError !== null && (
            <Alert severity="error" role="alert">
              {submitError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void confirm()} disabled={confirmDisabled}>
          {submitting ? 'Linking...' : 'Confirm link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" sx={{ gap: 1.5, justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Stack>
  );
}

function blockerCopy(blocker: SubscriptionRelinkBlocker): string {
  switch (blocker) {
    case 'canceled':
      return 'This subscription is canceled. Sell a new membership instead.';
    case 'creating':
      return 'This subscription is still creating. Retry shortly.';
    case 'wrong-member':
      return 'This subscription belongs to another member.';
  }
}
